import { S3Bucket } from "./s3_bucket";

import { S3_BUCKETS } from "config";
import { RoleType, UserType } from "shared/types/user";

const _removeNonLetters = (str: string) => str.replace(/[^a-zA-Z]/g, "");

export function computeInitials(user: UserType | null) {
  if (!user?.first_name || !user?.last_name) return "";
  const firstName = _removeNonLetters(user.first_name);
  const lastName = _removeNonLetters(user.last_name);
  return `${firstName ? firstName[0].toUpperCase() : ""}${
    lastName ? lastName[0].toUpperCase() : ""
  }`;
}

function _getFullName(user: UserType | null) {
  if (!user) return "";
  const { first_name, last_name } = user;
  return `${first_name} ${last_name}`;
}

function _getTitledName(user: UserType | null) {
  if (!user?.title) return "";
  return `${user.title} ${user.last_name}`;
}

export function longDisplayName(user: UserType | null) {
  return _getTitledName(user) || _getFullName(user);
}

export function shortDisplayName(user: UserType | null) {
  return _getTitledName(user) || user?.first_name || "";
}

export function profilePhotoUrl(user: UserType | null) {
  if (!user?.aws_avatar_key || user.aws_avatar_key === "default-avatar-wide.png") return "";
  const s3Bucket = new S3Bucket(S3_BUCKETS.PICTURES);
  return user.aws_avatar_key && s3Bucket.getS3Url(user.aws_avatar_key);
}

export function getNameForSorting(user: UserType | null) {
  const displayName = longDisplayName(user);
  // Make names composed entirely of symbols come first
  if (!displayName.replace(/[^a-zA-Z]/g, "")) return " ";
  return displayName.toUpperCase();
}

export function getOrderForRole(user: UserType | null) {
  const roleToOrder: { [key: string]: number } = {
    [RoleType.Teacher]: 1,
    [RoleType.Assistant]: 2,
    [RoleType.GuestInstructor]: 3,
    [RoleType.BackupInstructor]: 4,
    [RoleType.TA]: 5,
    [RoleType.Captioner]: 6,
    [RoleType.Student]: 7,
    [RoleType.Observer]: 8,
  };
  return (user && roleToOrder[user.role]) || 9;
}

export function getRoleAndNameForSorting(user: UserType | null) {
  return getOrderForRole(user) + getNameForSorting(user);
}

export function roleForDisplay(user: UserType | null) {
  const roleToDisplay: { [key: string]: string } = {
    [RoleType.Teacher]: "Instructor",
    [RoleType.Assistant]: "Assistant",
    [RoleType.GuestInstructor]: "Guest Instructor",
    [RoleType.BackupInstructor]: "Backup Instructor",
    [RoleType.TA]: "TA",
    [RoleType.Student]: "Student",
    [RoleType.Captioner]: "Captioner",
  };

  // Anyone with a role not in the above list is observing
  return (user && roleToDisplay[user.role]) || "Observer";
}

export function shortRoleForDisplay(user: UserType | null) {
  const roleToDisplay: { [key: string]: string } = {
    [RoleType.Teacher]: "Inst.",
    [RoleType.Assistant]: "Asst.",
    [RoleType.GuestInstructor]: "Guest Inst.",
    [RoleType.BackupInstructor]: "Backup Inst.",
    [RoleType.TA]: "TA",
    [RoleType.Student]: "Student",
    [RoleType.Captioner]: "Captioner",
  };

  // Anyone with a role not in the above list is observing
  return (user && roleToDisplay[user.role]) || "Observer";
}

// Used when only one user in class should handle a certain action
// IS_ALWAYS_SUBSCRIBED_TO (teacher, guest instructors, and assistants) > everyone else
// Prioritize by role from getOrderForRole
export function isLeader(userId: string, users: UserType[], isAlwaysSubscribedToUserIds: string[]) {
  const presentStarredUsers = users
    .filter((user) => user.is_present && isAlwaysSubscribedToUserIds.includes(user.id))
    .sort((a, b) => getOrderForRole(a) - getOrderForRole(b) || a.id.localeCompare(b.id))
    .map((user) => user.id);
  if (presentStarredUsers.length > 0) {
    return presentStarredUsers[0] === userId;
  }

  const presentNonStarredUsers = users
    .filter((user) => user.is_present && !isAlwaysSubscribedToUserIds.includes(user.id))
    .sort((a, b) => getOrderForRole(a) - getOrderForRole(b) || a.id.localeCompare(b.id))
    .map((user) => user.id);
  return presentNonStarredUsers.length === 0 || presentNonStarredUsers[0] === userId;
}

export function talkTimeForDisplay(talkTime: number | undefined) {
  return `${((talkTime || 0) / 1000).toFixed(0)}`;
}
